import type { ReactNode } from "react";
import { css, cx } from "@strust/ui/css";
import { alert, skeleton, spinner, state } from "@strust/ui/recipes";
import { slots } from "../slots.ts";
import {
  AlertTriangleIcon,
  CircleCheckIcon,
  InfoIcon,
  LockIcon,
  OctagonXIcon,
  RefreshIcon,
  SearchXIcon,
  ServerCrashIcon,
} from "../icons/icons.tsx";
import { Button } from "./button.tsx";

export type AlertTone = "info" | "success" | "warning" | "danger";

const ALERT_ICONS: Record<AlertTone, ReactNode> = {
  info: <InfoIcon />,
  success: <CircleCheckIcon />,
  warning: <AlertTriangleIcon />,
  danger: <OctagonXIcon />,
};

export interface AlertProps {
  readonly tone?: AlertTone;
  /** Short bold heading (optional). */
  readonly title?: string;
  readonly children?: ReactNode;
  /** Action rendered on the right (a link or button styled with `alertAction`). */
  readonly action?: ReactNode;
  readonly className?: string;
}

/**
 * Inline alert with a left rule. Danger and warning are announced immediately (`alert`); info and
 * success politely (`status`). Text always carries the meaning, color only reinforces it.
 */
export function Alert({ tone = "info", title, children, action, className }: AlertProps) {
  const s = alert({ tone });
  return (
    <div
      role={tone === "danger" || tone === "warning" ? "alert" : "status"}
      className={cx(s.root, className)}
      data-tone={tone}
    >
      <span aria-hidden="true" className={s.icon}>
        {ALERT_ICONS[tone]}
      </span>
      <div className={s.content}>
        {title ? <p className={s.title}>{title}</p> : null}
        {children ? <div className={s.description}>{children}</div> : null}
      </div>
      {action}
    </div>
  );
}

/** Class of an alert's action button or link, matching the alert's tone. */
export function alertActionClass(tone: AlertTone = "info"): string {
  return slots(alert({ tone })).action;
}

export interface SkeletonProps {
  readonly width?: string;
  readonly height?: string;
  readonly className?: string;
}

/** Decorative placeholder; the surrounding region carries `aria-busy`. */
export function Skeleton({ width = "100%", height = "0.75rem", className }: SkeletonProps) {
  return (
    <span aria-hidden="true" className={cx(skeleton(), className)} style={{ width, height }} />
  );
}

/** Small busy indicator; pair it with visible or visually hidden text. */
export function Spinner({ className }: { readonly className?: string }) {
  return <span aria-hidden="true" className={cx(spinner(), className)} />;
}

type StateTone = "empty" | "noAccess" | "error" | "info";

interface StateProps {
  readonly title: string;
  readonly description?: ReactNode;
  readonly action?: ReactNode;
  readonly icon?: ReactNode;
  /** Without the panel frame (inside a table or card). */
  readonly bare?: boolean;
  readonly className?: string;
}

function StateView({
  tone,
  title,
  description,
  action,
  icon,
  bare = false,
  className,
  role,
  defaultIcon,
}: StateProps & {
  readonly tone: StateTone;
  readonly role?: "alert" | "status";
  readonly defaultIcon: ReactNode;
}) {
  const s = state({ tone, bare });
  return (
    <div className={cx(s.root, className)} role={role} data-state={tone}>
      <span aria-hidden="true" className={s.glyph}>
        {icon ?? defaultIcon}
      </span>
      <p className={s.title}>{title}</p>
      {description ? <div className={s.body}>{description}</div> : null}
      {action ? <div className={s.action}>{action}</div> : null}
    </div>
  );
}

export type EmptyStateProps = StateProps;

/** Nothing to show (no results, empty list). */
export function EmptyState(props: EmptyStateProps) {
  return <StateView tone="empty" defaultIcon={<SearchXIcon />} {...props} />;
}

/** The user lacks the permission for this screen or action. */
export function NoAccessState(props: StateProps) {
  return <StateView tone="noAccess" defaultIcon={<LockIcon />} {...props} />;
}

export interface ErrorStateProps {
  readonly title: string;
  readonly retryLabel: string;
  readonly onRetry: () => void;
  readonly children?: ReactNode;
  readonly bare?: boolean;
  readonly className?: string;
}

/** Error with a retry action; announced to assistive technology. */
export function ErrorState({
  title,
  retryLabel,
  onRetry,
  children,
  bare,
  className,
}: ErrorStateProps) {
  return (
    <StateView
      tone="error"
      role="alert"
      defaultIcon={<ServerCrashIcon />}
      title={title}
      description={children}
      {...(bare === undefined ? {} : { bare })}
      {...(className === undefined ? {} : { className })}
      action={
        <Button size="sm" icon={<RefreshIcon />} onClick={onRetry}>
          {retryLabel}
        </Button>
      }
    />
  );
}

export interface LoadingStateProps {
  /** Announced to assistive technology while the content loads. */
  readonly label: string;
  readonly lines?: number;
  readonly bare?: boolean;
}

/** Skeleton block for a loading region (`aria-busy` with a visually hidden label). */
export function LoadingState({ label, lines = 5, bare = false }: LoadingStateProps) {
  const s = state({ tone: "empty", bare });
  const widths = ["40%", "100%", "75%", "100%", "55%", "90%", "65%"];
  return (
    <div
      className={cx(
        s.root,
        css({ alignItems: "stretch", justifyContent: "flex-start", gap: "3.5" }),
      )}
      aria-busy="true"
      role="status"
    >
      <span className={css({ srOnly: true })}>{label}</span>
      {Array.from({ length: lines }, (_, index) => (
        <Skeleton key={index} width={widths[index % widths.length] ?? "100%"} />
      ))}
    </div>
  );
}
